/* ==========================================================================
   NUDE Cacao PDP: behaviour
   Scope: product template `nude-graza` only.

   Design notes
   - Every module is idempotent. Elements are stamped with a data flag so a
     theme-editor reload (shopify:section:load) can never bind twice.
   - Nothing essential lives behind JavaScript. Accordion panels, gallery
     slides and composition panels are all in the DOM and are revealed by
     CSS when JS is unavailable (html.no-js).
   - Cart behaviour is NOT reimplemented here. The buy form is a native
     <product-form> element, so the theme's own cart drawer, error handling
     and pub/sub events keep working exactly as before.
   ========================================================================== */

(function () {
  'use strict';

  var FLAG = 'npdpBound';
  var observers = [];

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  function bindOnce(el, fn) {
    if (!el || el.dataset[FLAG] === 'true') return false;
    el.dataset[FLAG] = 'true';
    fn(el);
    return true;
  }

  /* --------------------------------------------------------------------
     Inline accordions (buy column + FAQ)
     -------------------------------------------------------------------- */

  function initAccordions(scope) {
    each(scope.querySelectorAll('[data-npdp-acc]'), function (root) {
      bindOnce(root, function () {
        var buttons = root.querySelectorAll('.npdp-acc__btn');
        each(buttons, function (btn) {
          btn.addEventListener('click', function () {
            var expanded = btn.getAttribute('aria-expanded') === 'true';
            var panel = document.getElementById(btn.getAttribute('aria-controls'));
            btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
            if (panel) panel.hidden = expanded;
          });
        });
      });
    });
  }

  /* --------------------------------------------------------------------
     Purchase-area testing link → opens the named accordion IN PLACE
     (Pass F, 17 Aug 2026). The trigger keeps a real href as its no-JS
     fallback; with JS it expands the accordion row whose container
     carries data-npdp-acc-key equal to the trigger's data-npdp-open-acc,
     scrolls to it and moves focus to the accordion button so the ARIA
     state change is announced.
     -------------------------------------------------------------------- */

  function initProofToggle(scope) {
    each(scope.querySelectorAll('[data-npdp-open-acc]'), function (link) {
      bindOnce(link, function () {
        link.addEventListener('click', function (event) {
          var key = link.getAttribute('data-npdp-open-acc');
          if (!key) return;
          var row = document.querySelector('[data-npdp-acc-key="' + key + '"]');
          if (!row) return; // fall through to the href
          var btn = row.querySelector('.npdp-acc__btn');
          if (!btn) return;
          event.preventDefault();
          var panel = document.getElementById(btn.getAttribute('aria-controls'));
          btn.setAttribute('aria-expanded', 'true');
          if (panel) panel.hidden = false;
          var reduce =
            window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          row.scrollIntoView({
            behavior: reduce ? 'auto' : 'smooth',
            block: 'center'
          });
          try {
            btn.focus({ preventScroll: true });
          } catch (e) {
            btn.focus();
          }
        });
      });
    });
  }

  /* --------------------------------------------------------------------
     Product gallery · round 2 (18 Aug 2026, Ali's PDP brief)
     The stage is a native scroll-snap carousel: finger-swipe on touch,
     arrow buttons on desktop, dots on mobile, thumbnails as the desktop
     quick-jump. One shared index keeps every control honest, whichever
     way the user moves — swipe, arrow, dot, thumb or arrow key.
     Programmatic jumps are INSTANT (behavior auto): smooth programmatic
     scrolling on a snap container proved unreliable (animations cancelled
     back to origin under page zoom). Touch swiping is native regardless.
     Scroll sync uses a plain timeout, not requestAnimationFrame — rAF is
     throttled to a standstill in some embedded/low-power contexts and the
     sync must never wedge.
     -------------------------------------------------------------------- */

  function initGallery(scope) {
    each(scope.querySelectorAll('[data-npdp-gallery]'), function (root) {
      bindOnce(root, function () {
        var stage = root.querySelector('.npdp-gal__stage');
        var slides = root.querySelectorAll('[data-npdp-slide]');
        var thumbs = root.querySelectorAll('[data-npdp-thumb]');
        var dots = root.querySelectorAll('[data-npdp-dot]');
        var prev = root.querySelector('[data-npdp-gal-prev]');
        var next = root.querySelector('[data-npdp-gal-next]');
        if (!stage || !slides.length) return;

        var current = 0;

        function mark(index) {
          current = index;
          each(slides, function (slide, i) {
            slide.setAttribute('data-active', i === index ? 'true' : 'false');
          });
          each(thumbs, function (thumb, i) {
            thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
          });
          each(dots, function (dot, i) {
            dot.setAttribute('aria-current', i === index ? 'true' : 'false');
          });
          if (prev) prev.disabled = index === 0;
          if (next) next.disabled = index === slides.length - 1;
        }

        function goTo(index) {
          if (index < 0) index = 0;
          if (index > slides.length - 1) index = slides.length - 1;
          stage.scrollTo({ left: slides[index].offsetLeft, behavior: 'auto' });
          mark(index);
        }

        var pending = false;
        stage.addEventListener(
          'scroll',
          function () {
            if (pending) return;
            pending = true;
            window.setTimeout(function () {
              pending = false;
              if (!stage.clientWidth) return;
              var i = Math.round(stage.scrollLeft / stage.clientWidth);
              if (i !== current && i >= 0 && i < slides.length) mark(i);
            }, 80);
          },
          { passive: true }
        );

        each(thumbs, function (thumb, i) {
          thumb.addEventListener('click', function () {
            goTo(i);
          });
          thumb.addEventListener('keydown', function (event) {
            var n = null;
            if (event.key === 'ArrowRight') n = i + 1;
            if (event.key === 'ArrowLeft') n = i - 1;
            if (n === null) return;
            if (n < 0) n = thumbs.length - 1;
            if (n > thumbs.length - 1) n = 0;
            event.preventDefault();
            thumbs[n].focus();
            goTo(n);
          });
        });

        each(dots, function (dot, i) {
          dot.addEventListener('click', function () {
            goTo(i);
          });
        });

        if (prev) prev.addEventListener('click', function () { goTo(current - 1); });
        if (next) next.addEventListener('click', function () { goTo(current + 1); });

        mark(0);
      });
    });
  }

  /* --------------------------------------------------------------------
     Purchase options: main and sticky controls always agree
     -------------------------------------------------------------------- */

  function initBuy(scope) {
    each(scope.querySelectorAll('[data-npdp-buy]'), function (root) {
      bindOnce(root, function () {
        var radios = root.querySelectorAll('input[name="id"]');
        var form = root.querySelector('form[action*="/cart/add"]');
        var formId = root.getAttribute('data-npdp-form-id');

        // The main button lives inside this section. The sticky bar and the
        // final close button submit the same form through the HTML `form`
        // attribute, so the target list is recomputed on every sync: a section
        // added later in the theme editor is picked up without a reload.
        function targets() {
          var found = [];
          each(document.querySelectorAll('[data-npdp-atc]'), function (btn) {
            if (btn.getAttribute('data-npdp-form') === formId) found.push(btn);
          });
          return found;
        }

        function sync() {
          var checked = root.querySelector('input[name="id"]:checked');
          if (!checked) return;
          var available = checked.getAttribute('data-available') === 'true';
          var label = available
            ? checked.getAttribute('data-label-available')
            : checked.getAttribute('data-label-soldout');
          var price = checked.getAttribute('data-price-label') || '';

          each(targets(), function (btn) {
            var text = btn.querySelector('[data-npdp-atc-label]');
            if (text) text.textContent = label;
            btn.disabled = !available;
            btn.setAttribute('aria-disabled', available ? 'false' : 'true');
          });

          each(document.querySelectorAll('[data-npdp-sticky-price]'), function (el) {
            el.textContent = price;
          });
        }

        each(radios, function (radio) {
          radio.addEventListener('change', sync);
        });

        if (form) {
          form.addEventListener('submit', function () {
            var list = targets();
            each(list, function (btn) {
              btn.classList.add('is-loading');
            });
            window.setTimeout(function () {
              each(list, function (btn) {
                btn.classList.remove('is-loading');
              });
            }, 2500);
          });
        }

        root.npdpSync = sync;
        sync();
      });
    });
  }

  /* --------------------------------------------------------------------
     Sticky purchase bar visibility
     -------------------------------------------------------------------- */

  function initSticky(scope) {
    each(scope.querySelectorAll('[data-npdp-sticky]'), function (bar) {
      bindOnce(bar, function () {
        var anchorId = bar.getAttribute('data-npdp-sticky-anchor');
        var anchor = anchorId ? document.getElementById(anchorId) : null;
        if (!anchor || typeof window.IntersectionObserver !== 'function') {
          bar.setAttribute('data-visible', 'false');
          return;
        }
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              var past = entry.boundingClientRect.top < 0 && !entry.isIntersecting;
              bar.setAttribute('data-visible', past ? 'true' : 'false');
            });
          },
          { rootMargin: '0px 0px -40% 0px', threshold: 0 }
        );
        observer.observe(anchor);
        observers.push(observer);
      });
    });
  }

  /* --------------------------------------------------------------------
     Composition chapter: measured component selector
     The controls are real radio inputs inside a labelled group, so the
     selected state is exposed natively and works without this script.
     -------------------------------------------------------------------- */

  function initComposition(scope) {
    each(scope.querySelectorAll('[data-npdp-composition]'), function (root) {
      bindOnce(root, function () {
        var radios = root.querySelectorAll('input[data-npdp-metric]');
        var panels = root.querySelectorAll('[data-npdp-panel]');
        if (!radios.length || !panels.length) return;

        function show(key) {
          each(panels, function (panel) {
            panel.setAttribute(
              'data-active',
              panel.getAttribute('data-npdp-panel') === key ? 'true' : 'false'
            );
          });
        }

        each(radios, function (radio) {
          radio.addEventListener('change', function () {
            if (radio.checked) show(radio.getAttribute('data-npdp-metric'));
          });
        });

        var checked = root.querySelector('input[data-npdp-metric]:checked') || radios[0];
        if (checked) {
          checked.checked = true;
          show(checked.getAttribute('data-npdp-metric'));
        }
      });
    });
  }


  /* --------------------------------------------------------------------
     SOURCES & NOTES disclosures

     The markup ships expanded so that, without this script, every citation
     marker still resolves to a visible list item. Here we collapse them and
     make the markers open the right one.
     -------------------------------------------------------------------- */

  function initNotes(scope) {
    each(scope.querySelectorAll('.npdp-notes__btn'), function (btn) {
      bindOnce(btn, function () {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;
        btn.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
        btn.addEventListener('click', function () {
          var open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', open ? 'false' : 'true');
          panel.hidden = open;
        });
      });
    });
  }

  function openNotesFor(target) {
    var panel = target.closest('.npdp-notes__panel');
    if (!panel) return;
    var btn = document.getElementById(panel.getAttribute('aria-labelledby'));
    if (btn && btn.getAttribute('aria-expanded') !== 'true') {
      btn.setAttribute('aria-expanded', 'true');
    }
    panel.hidden = false;
  }

  function initCitations(scope) {
    each(scope.querySelectorAll('[data-npdp-cite]'), function (link) {
      bindOnce(link, function () {
        link.addEventListener('click', function (event) {
          var id = (link.getAttribute('href') || '').slice(1);
          var target = id ? document.getElementById(id) : null;
          if (!target) return; // let the browser do whatever it would have done
          event.preventDefault();
          openNotesFor(target);
          each(document.querySelectorAll('.npdp-notes__list li.is-cited'), function (li) {
            li.classList.remove('is-cited');
          });
          target.classList.add('is-cited');
          target.scrollIntoView({ block: 'nearest' });
          try {
            target.focus({ preventScroll: true });
          } catch (e) {
            target.focus();
          }
        });
      });
    });
  }

  /* --------------------------------------------------------------------
     Tasting dimensions

     Hover, keyboard focus and tap all reveal the same description in a
     fixed-height region, so nothing below it can move.
     -------------------------------------------------------------------- */

  function initDimensions(scope) {
    each(scope.querySelectorAll('[data-npdp-dims]'), function (root) {
      bindOnce(root, function () {
        var chips = root.querySelectorAll('[data-npdp-dim]');
        var out = root.querySelector('[data-npdp-dim-out]');
        if (!chips.length || !out) return;

        var pinned = chips[0];

        function paint(chip) {
          out.textContent = chip.getAttribute('data-npdp-dim-text') || '';
        }

        function pin(chip) {
          pinned = chip;
          each(chips, function (c) {
            c.setAttribute('aria-pressed', String(c === chip));
          });
          paint(chip);
        }

        each(chips, function (chip) {
          chip.addEventListener('mouseenter', function () { paint(chip); });
          chip.addEventListener('focus', function () { paint(chip); });
          chip.addEventListener('mouseleave', function () { paint(pinned); });
          chip.addEventListener('blur', function () { paint(pinned); });
          chip.addEventListener('click', function () { pin(chip); });
        });

        pin(pinned);
      });
    });
  }

  /* --------------------------------------------------------------------
     Reduced motion: stop the looping product-piece video outright rather
     than relying on CSS alone.
     -------------------------------------------------------------------- */

  function initMotion(scope) {
    if (!window.matchMedia) return;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!reduce.matches) return;
    each(scope.querySelectorAll('[data-npdp-loop]'), function (video) {
      bindOnce(video, function () {
        video.removeAttribute('autoplay');
        video.autoplay = false;
        video.loop = false;
        try {
          video.pause();
          video.currentTime = 0;
        } catch (e) {
          /* no-op */
        }
      });
    });
  }

  /* -------------------------------------------------------------------- */

  function init(scope) {
    var root = scope || document;
    initAccordions(root);
    initProofToggle(root);
    initGallery(root);
    initBuy(root);
    initSticky(root);
    initComposition(root);
    initNotes(root);
    initCitations(root);
    initDimensions(root);
    initMotion(root);

    // Re-label any purchase button that appeared after the buy section bound.
    each(document.querySelectorAll('[data-npdp-buy]'), function (buy) {
      if (typeof buy.npdpSync === 'function') buy.npdpSync();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
    init(document);
  });

  document.addEventListener('shopify:section:unload', function () {
    observers = observers.filter(function (observer) {
      observer.disconnect();
      return false;
    });
    init(document);
  });
})();
